import { useState } from "react";
import { useQuery } from "convex/react";
import { Hash, MessageSquare, Plus, Search } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import type { Doc, Id } from "../../../convex/_generated/dataModel";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { UserMenu } from "@/components/auth/UserMenu";
import { NewConversationDialog } from "@/components/chat/NewConversationDialog";

type Props = {
  meId: Id<"users">;
  activeId: Id<"conversations"> | null;
  setActiveId: (id: Id<"conversations">) => void;
  openSearch: () => void;
};

export function ChatSidebar({ meId, activeId, setActiveId, openSearch }: Props) {
  const [dialog, setDialog] = useState<"room" | "dm" | null>(null);
  const list = useQuery(api.conversations.listMine);

  const rooms = (list ?? []).filter((i) => i.conversation.kind === "room");
  const dms = (list ?? []).filter((i) => i.conversation.kind === "dm");

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-between px-2 py-1">
          <span className="font-semibold">Chat</span>
          <button
            onClick={openSearch}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Search"
          >
            <Search className="size-4" />
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Rooms</SidebarGroupLabel>
          <SidebarGroupAction
            title="New room"
            onClick={() => setDialog("room")}
          >
            <Plus />
          </SidebarGroupAction>
          <SidebarGroupContent>
            <SidebarMenu>
              {rooms.length === 0 ? (
                <EmptyHint icon={<Hash className="size-3.5" />} text="No rooms yet" />
              ) : (
                rooms.map((item) => (
                  <RoomItem
                    key={item.conversation._id}
                    item={item}
                    active={activeId === item.conversation._id}
                    onClick={() => setActiveId(item.conversation._id)}
                  />
                ))
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Direct messages</SidebarGroupLabel>
          <SidebarGroupAction title="New DM" onClick={() => setDialog("dm")}>
            <Plus />
          </SidebarGroupAction>
          <SidebarGroupContent>
            <SidebarMenu>
              {dms.length === 0 ? (
                <EmptyHint
                  icon={<MessageSquare className="size-3.5" />}
                  text="No DMs yet"
                />
              ) : (
                dms.map((item) => (
                  <DmItem
                    key={item.conversation._id}
                    item={item}
                    active={activeId === item.conversation._id}
                    onClick={() => setActiveId(item.conversation._id)}
                  />
                ))
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <UserMenu />
      </SidebarFooter>

      <NewConversationDialog
        mode={dialog}
        onClose={() => setDialog(null)}
        onCreated={(id: Id<"conversations">) => {
          setActiveId(id);
          setDialog(null);
        }}
      />
      {/* meId may be useful in future for filtering; reference to silence lints */}
      <span className="hidden">{meId}</span>
    </Sidebar>
  );
}

type ListItem = {
  conversation: Doc<"conversations">;
  member: Doc<"conversationMembers">;
  peer: { _id: Id<"users">; name?: string; email?: string; image?: string } | null;
  unread: boolean;
};

function RoomItem({
  item,
  active,
  onClick,
}: {
  item: ListItem;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton isActive={active} onClick={onClick}>
        <Hash className="size-4" />
        <span className="truncate flex-1">
          {item.conversation.name ?? "Untitled"}
        </span>
        {item.unread ? <Badge variant="default" className="h-4 px-1 text-[10px]">new</Badge> : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function DmItem({
  item,
  active,
  onClick,
}: {
  item: ListItem;
  active: boolean;
  onClick: () => void;
}) {
  const initials = (item.peer?.name ?? item.peer?.email ?? "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton isActive={active} onClick={onClick}>
        <Avatar className="size-5">
          {item.peer?.image ? <AvatarImage src={item.peer.image} /> : null}
          <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
        </Avatar>
        <span className="truncate flex-1">
          {item.peer?.name ?? item.peer?.email ?? "Unknown"}
        </span>
        {item.unread ? <Badge variant="default" className="h-4 px-1 text-[10px]">new</Badge> : null}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

function EmptyHint({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <SidebarMenuItem>
      <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
        {icon}
        {text}
      </div>
    </SidebarMenuItem>
  );
}
